/**
 * The sites the operations dashboard's map paints: every site the page's
 * current filters still admit, fetched whole so panning and zooming the map
 * never waits on the network.
 *
 * Contains:
 * - useOperationsMapSites — the fetched rows plus the fit signal for the map
 *
 * Returns plain JS — no JSX.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

import { fetchDashboardMapSites } from '@sophocles/features/dashboards/api';

import buildOperationsMapParams from './operationsMapParams';

/**
 * Fetch the dashboard map's sites, refetching whenever the customer or the
 * filters change and dropping any response a newer request has overtaken.
 *
 * `fitKey` changes once per landed dataset, which is the signal `SiteMap` fits
 * on; `refit` re-frames the map without refetching, for the page's reset action.
 *
 * @param {string|number} customerId
 * @param {object} filters - the dashboard's map filter state.
 * @returns {{ sites: object[], fitKey: number, refit: () => void }}
 */
export default function useOperationsMapSites(customerId, filters) {
  const [sites, setSites] = useState([]);
  const [fitKey, setFitKey] = useState(0);
  const requestIdRef = useRef(0);

  const refit = useCallback(() => setFitKey(key => key + 1), []);

  // The filter object is rebuilt on every render, so the effect keys off its
  // content and reads the latest object through a ref.
  const filtersKey = JSON.stringify(filters ?? {});
  const filtersRef = useRef(filters);
  filtersRef.current = filters;

  useEffect(() => {
    if (!customerId) {
      setSites([]);
      return;
    }
    const myId = ++requestIdRef.current;
    fetchDashboardMapSites(buildOperationsMapParams(customerId, filtersRef.current))
      .then(data => {
        if (requestIdRef.current !== myId) return;
        setSites(Array.isArray(data?.sites) ? data.sites : []);
        setFitKey(key => key + 1);
      })
      // A failed refresh leaves the markers that are already on the map: the
      // dashboard is a wallboard surface, and a blank map reads as "no sites"
      // rather than "the last refresh did not land".
      .catch(() => {});
  }, [customerId, filtersKey]);

  return { sites, fitKey, refit };
}
